import React, { useState, useEffect } from 'react'
import './event_screen.css'
import AppBarWidget from '../../widgets/app_bar/app_bar';
import { useSelector, useDispatch } from 'react-redux';
import CreateEventDialog from './create_project_dialog/create_event_dialog';
import EventView from './event_view/event_view';
import PagesTitle from '../../widgets/pages_title/pages_title';
import AddIcon from '@mui/icons-material/Add';
import { changeGridView, selectEvent, setProjectEvents, deleteEventByID } from '../../redux/project/projects';
import { getEventsAPI, deleteEventAPI } from '../../api/event';
import { HideProgressAlert, showProgressAlert } from '../../functions/show_progress_page';
import { hundleRequestFailure } from '../../functions/hundle_request_failure';
import { showAlert } from '../../functions/show_alert';
import GridEvent from './grid-view/grid_event';
import ListEvent from './list-view/list_event';
import { requestState } from '../../global/http_enum';
import ResponseHttp from '../../widgets/response_http/response_http';

export default function EventScreen() {
    const projectSelected = useSelector(state => state.projectSlice.projectSelected);
    const gridview = useSelector(state => state.projectSlice.gridview);
    const dispatch = useDispatch();
    const [showCreateEvent, SetShowCreateEvent] = useState(false);
    const [isWizardShown, setIsWizardShown] = useState(false);
    const [selectedEventId, setSelectedEventId] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [eventsRequestState, setEventsRequestState] = useState(requestState.initial);
    const [events, setEvents] = useState([]);

    useEffect(() => {
        const loadEvents = async () => {
            if (!projectSelected || !projectSelected.id) return;

            // Check if events are already loaded in Redux (including empty arrays)
            if (projectSelected.events && Array.isArray(projectSelected.events)) {
                setEvents(projectSelected.events);
                setEventsRequestState(requestState.haveData);
                return;
            }

            setEventsRequestState(requestState.initial);
            showProgressAlert();
            const response = await getEventsAPI(projectSelected.id);
            HideProgressAlert();

            if (response.statusCode === 200) {
                const eventsData = Array.isArray(response.response) ? response.response : [];
                setEvents(eventsData);
                dispatch(setProjectEvents({ id: projectSelected.id, events: eventsData }));
                // If there was a selectedEvent, ensure it's re-selected with the latest data
                const currentSelectedEvent = projectSelected?.selectedEvent;
                if (currentSelectedEvent?.id) {
                    dispatch(selectEvent({ eventId: currentSelectedEvent.id }));
                }
                setEventsRequestState(requestState.haveData);
            } else {
                hundleRequestFailure(response);
                setEventsRequestState(requestState.serverError);
            }
        };

        if (projectSelected && projectSelected.id) {
            loadEvents();
        }
    }, [projectSelected?.id, projectSelected, dispatch]);

    const handleSelectEvent = (event) => {
        setSelectedEventId(event.id);
        dispatch(selectEvent({ eventId: event.id }));
    };

    const handleOpenEvent = (event) => {
        setSelectedEvent(event);
        setSelectedEventId(event.id);
        dispatch(selectEvent({ eventId: event.id }));
    };

    const handleCreateEvent = () => {
        SetShowCreateEvent(true);
        setSelectedEvent(null);
        setSelectedEventId(null);
    };

    const handleBackToList = () => {
        SetShowCreateEvent(false);
        setIsWizardShown(false);
        setSelectedEvent(null);
        setSelectedEventId(null);
        // Reload events after creating
        if (projectSelected && projectSelected.id) {
            const reloadEvents = async () => {
                showProgressAlert();
                const response = await getEventsAPI(projectSelected.id);
                HideProgressAlert();
                if (response.statusCode === 200) {
                    const eventsData = Array.isArray(response.response) ? response.response : [];
                    setEvents(eventsData);
                    dispatch(setProjectEvents({ id: projectSelected.id, events: eventsData }));
                }
            };
            reloadEvents();
        }
    };

    const handleDeleteEvent = async (eventId) => {
        if (!projectSelected || !projectSelected.id) return;

        showProgressAlert();
        const response = await deleteEventAPI(projectSelected.id, eventId);
        HideProgressAlert();

        if (response.statusCode < 300) {
            // Clean up localStorage data for this event (event-problem mapping)
            try {
                const getStorageKey = () => `eventProblems_${projectSelected.id || 'default'}`;
                const stored = localStorage.getItem(getStorageKey());
                if (stored) {
                    const parsed = JSON.parse(stored);
                    const eventProblemMap = Array.isArray(parsed) ? new Map(parsed) : new Map(Object.entries(parsed));
                    eventProblemMap.delete(eventId);
                    localStorage.setItem(getStorageKey(), JSON.stringify(Array.from(eventProblemMap.entries())));
                }
            } catch (error) {
                console.error('Failed to clean up localStorage for event:', error);
            }

            // Update Redux state
            dispatch(deleteEventByID({ projectId: projectSelected.id, eventId: eventId }));

            // Update local state
            const updatedEvents = events.filter(e => e.id !== eventId);
            setEvents(updatedEvents);
            dispatch(setProjectEvents({ id: projectSelected.id, events: updatedEvents }));

            // Clear selected event if it was deleted
            if (selectedEventId === eventId) {
                setSelectedEvent(null);
                setSelectedEventId(null);
                dispatch(selectEvent({ eventId: null }));
            }

            showAlert(true, 'Event deleted successfully');
        } else {
            hundleRequestFailure(response);
        }
    };

    // If viewing a specific event
    if (selectedEvent && !showCreateEvent) {
        return (
            <>
                <AppBarWidget />
                <div id='event-screen'>
                    <EventView id={projectSelected.id} event={selectedEvent} onBack={handleBackToList} />
                </div>
            </>
        );
    }

    // If creating a new event
    if (showCreateEvent) {
        return (
            <>
                {!isWizardShown && (
                    <>
                        <AppBarWidget />
                        <div id="event-screen-top">
                            <PagesTitle title={projectSelected?.name || 'Events'} />
                            <div id="event-buttons">
                                <div onClick={handleCreateEvent} className="create-event">
                                    <div className="create-event-icon-wrapper-top">
                                        <AddIcon className="create-event-icon-top" />
                                    </div>
                                    <div className="create-event-text-top">Create Event</div>
                                </div>
                                <div className="grid-view-holder">
                                    Grid View
                                    <input
                                        onChange={() => dispatch(changeGridView())}
                                        defaultChecked={gridview}
                                        value={gridview}
                                        className='grid-view'
                                        type="checkbox"
                                    />
                                </div>
                            </div>
                        </div>
                        <div id='event-screen'>
                            <EventsViewWidget
                                gridView={gridview}
                                httpResponse={eventsRequestState}
                                selectedEventId={selectedEventId}
                                events={events}
                                handleSelectEvent={handleSelectEvent}
                                handleOpenEvent={handleOpenEvent}
                                handleCreateEvent={handleCreateEvent}
                                handleDeleteEvent={handleDeleteEvent}
                            />
                        </div>
                    </>
                )}
                <CreateEventDialog
                    key="create-event-dialog"
                    projectSelected={projectSelected}
                    onClose={handleBackToList}
                    onWizardStateChange={setIsWizardShown}
                />
            </>
        );
    }

    // Show events list/grid
    return (
        <>
            <AppBarWidget />
            <div id="event-screen-top">
                <PagesTitle title={projectSelected?.name || 'Events'} />
                <div id="event-buttons">
                    <div onClick={handleCreateEvent} className="create-event">
                        <div className="create-event-icon-wrapper-top">
                            <AddIcon className="create-event-icon-top" />
                        </div>
                        <div className="create-event-text-top">Create Event</div>
                    </div>
                    <div className="grid-view-holder">
                        Grid View
                        <input
                            onChange={() => dispatch(changeGridView())}
                            defaultChecked={gridview}
                            value={gridview}
                            className='grid-view'
                            type="checkbox"
                        />
                    </div>
                </div>
            </div>
            <div id='event-screen'>
                <EventsViewWidget
                    gridView={gridview}
                    httpResponse={eventsRequestState}
                    selectedEventId={selectedEventId}
                    events={events}
                    handleSelectEvent={handleSelectEvent}
                    handleOpenEvent={handleOpenEvent}
                    handleCreateEvent={handleCreateEvent}
                    handleDeleteEvent={handleDeleteEvent}
                />
            </div>
        </>
    );
}

const EventsViewWidget = ({ httpResponse, selectedEventId, gridView, events, handleSelectEvent, handleOpenEvent, handleCreateEvent, handleDeleteEvent }) => {
    const [picked, SetPicked] = useState(-1);

    const deleteEvent = async (id) => {
        await handleDeleteEvent(id);
        SetPicked(-1);
    };

    if (requestState.haveData === httpResponse) {
        var Selected = selectedEventId || '';
        if (gridView) {
            return <div className={`main-events ${gridView ? 'main-events-grid ' : 'main-events-list'}`}>
                {events.map((e, index) => (
                    <GridEvent
                        deleteEvent={SetPicked}
                        SetEditEvent={() => { }}
                        Selected={Selected}
                        key={e.id || index}
                        eventData={e}
                        handleSelectEvent={handleSelectEvent}
                        handleOpenEvent={handleOpenEvent}
                    />
                ))}
                <div onClick={handleCreateEvent} className="create-event-grid-view">
                    <div className="create-event-icon-wrapper">
                        <AddIcon className="create-event-icon" />
                    </div>
                    <div className="create-event-text">Create Event</div>
                </div>
                {picked === -1 ? <></> : <div onClick={() => SetPicked(-1)} id="delete-item">
                    <div className="alert-mesg">
                        <h1>Are you sure You want to delete This Event ?</h1>
                        <div className="buttons">
                            <div onClick={() => SetPicked(-1)} className="bt close">Close</div>
                            <div onClick={() => deleteEvent(picked)} className="bt done">Confirm</div>
                        </div>
                    </div>
                </div>}
            </div>
        } else {
            return <div className={`main-events ${gridView ? 'main-events-grid ' : 'main-events-list'}`}>
                {events.map((e, index) => (
                    <ListEvent
                        deleteEvent={SetPicked}
                        SetEditEvent={() => { }}
                        Selected={Selected}
                        key={e.id || index}
                        eventData={e}
                        handleSelectEvent={handleSelectEvent}
                        handleOpenEvent={handleOpenEvent}
                    />
                ))}
                {picked === -1 ? <></> : <div onClick={() => SetPicked(-1)} id="delete-item">
                    <div className="alert-mesg">
                        <h1>Are you sure You want to delete This Event ?</h1>
                        <div className="buttons">
                            <div onClick={() => SetPicked(-1)} className="bt close">Close</div>
                            <div onClick={() => deleteEvent(picked)} className="bt done">Confirm</div>
                        </div>
                    </div>
                </div>}
            </div>
        }
    } else {
        return <ResponseHttp response={httpResponse} />
    }
}



